import React, { ReactNode, useId } from 'react';
import { SectionCard } from '@/components/ui/section-card';
import { Heading } from '@/components/ui/heading';
import { Text } from '@/components/ui/text';

// One numbered step of the widget generator. Every section on /widget
// (type → resort → settings → preview → terms → code) renders through
// this so the page reads as a single guided flow: same card chrome,
// same compact heading, same gap between the header and the controls.
//
// `titleId` is exposed so a react-select (which can't take a <Label>)
// can point aria-labelledby at the step heading.

type WidgetStepProps = {
  step: number;
  title: string;
  description?: string;
  titleId?: string;
  className?: string;
  children: ReactNode;
};

export function WidgetStep({
  step, title, description, titleId, className, children,
}: WidgetStepProps) {
  const autoId = useId();
  const headingId = titleId ?? autoId;

  return (
    <SectionCard aria-labelledby={headingId} className={className}>
      <header className="mb-4 flex items-start gap-3">
        <span
          aria-hidden
          className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground"
        >
          {step}
        </span>
        <div className="min-w-0">
          <Heading level={2} size="lg" id={headingId}>{title}</Heading>
          {description && (
            <Text variant="muted" className="mt-0.5 text-sm">{description}</Text>
          )}
        </div>
      </header>
      {children}
    </SectionCard>
  );
}

export default WidgetStep;
